import { create } from "zustand";

import type { McpInitializeResult } from "@/lib/mcp/types";

export type McpSessionStatus = "idle" | "connecting" | "connected" | "error";

export interface McpSessionRecord {
  sessionId?: string;
  status: McpSessionStatus;
  error?: string;
  lastConnectedAt?: number;
}

const defaultSessionState: McpSessionRecord = {
  status: "idle",
};

export interface McpSessionStore {
  sessions: Record<string, McpSessionRecord>;
  initialization: Record<string, McpInitializeResult | undefined>;
  setConnecting: (key: string) => void;
  setConnected: (key: string, sessionId?: string) => void;
  setError: (key: string, error: string) => void;
  clearSession: (key: string) => void;
  getSession: (key: string) => McpSessionRecord;
  setInitialization: (key: string, init: McpInitializeResult) => void;
  clearInitialization: (key: string) => void;
  getInitialization: (key: string) => McpInitializeResult | undefined;
}

export const useMcpSessionStore = create<McpSessionStore>((set, get) => ({
  sessions: {},
  initialization: {},

  setConnecting: (key) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [key]: {
          ...state.sessions[key],
          status: "connecting",
          error: undefined,
        },
      },
    }));
  },

  setConnected: (key, sessionId) => {
    const now = Date.now();
    set((state) => {
      const previous = state.sessions[key];
      const resolvedSessionId = sessionId ?? previous?.sessionId;
      const lastConnectedAt =
        previous?.status === "connected" &&
        previous?.sessionId === resolvedSessionId &&
        previous?.lastConnectedAt
          ? previous.lastConnectedAt
          : now;

      return {
        sessions: {
          ...state.sessions,
          [key]: {
            sessionId: resolvedSessionId,
            status: "connected",
            error: undefined,
            lastConnectedAt,
          },
        },
      };
    });
  },

  setError: (key, error) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [key]: {
          ...state.sessions[key],
          status: "error",
          error,
        },
      },
    }));
  },

  clearSession: (key) => {
    const existing = get().sessions[key];
    if (!existing) {
      return;
    }

    set((state) => {
      const next = { ...state.sessions };
      delete next[key];
      const nextInitialization = { ...state.initialization };
      delete nextInitialization[key];
      return { sessions: next, initialization: nextInitialization };
    });
  },

  getSession: (key) => {
    return get().sessions[key] ?? defaultSessionState;
  },

  setInitialization: (key, init) => {
    set((state) => ({
      initialization: {
        ...state.initialization,
        [key]: init,
      },
    }));
  },

  clearInitialization: (key) => {
    const existing = get().initialization[key];
    if (!existing) {
      return;
    }

    set((state) => {
      const next = { ...state.initialization };
      delete next[key];
      return { initialization: next };
    });
  },

  getInitialization: (key) => {
    return get().initialization[key];
  },
}));
